import React, { useEffect } from "react";
import {
  FaSyringe,
  FaPrescriptionBottleAlt,
  FaUserMd,
  FaStethoscope,
  FaNotesMedical,
  FaPills,
  FaFilePrescription,
  FaPlaneDeparture,
  FaHome,
  FaTruck,
  FaFileInvoice,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import Snowfall from "../components/ui/Snowfall";
import { Helmet } from "react-helmet-async";

export default function PharmacyServicesPage() {
  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  return (
    <div id="pharmacy" className="scroll-mt-28 relative">

      {/* ❄ Snow softly falling behind content */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Snowfall count={45} />
      </div>

      {/* INTRO */}
      <section
        className="relative z-10 py-20 px-6 bg-white text-gray-700 max-w-4xl mx-auto"
        data-aos="fade-up"
      >
        <Helmet>
          <title>Greenwich Pharmacy Services</title>
          <meta name="description" content="Pharmacy Services including Prescriptions, Vaccinations, Consultations, Prescribing Pharmacist, OAT, Compounding, Compliance Packaging, Travel Health Services, Home Care Supplies, Free Local Delivery, Uninsured Services, Injection Services" />
        </Helmet>

        {/* Header */}
        <div className="inline-block mb-8 relative">
          <h2 className="text-4xl md:text-5xl font-extrabold text-teal-800 tracking-tight">
            Pharmacy Services
          </h2>
          <div className="absolute w-24 h-1 bg-teal-600 bottom-0 left-0 rounded-full"></div>
        </div>

        {/* Description */}
        <div className="space-y-7 text-base md:text-lg leading-relaxed mt-4">
          <p className="flex items-start gap-3">
            <FaStethoscope className="text-teal-600 mt-1 text-xl" />
            Greenwich Pharmacy provides a full range of health services designed
            to make your care simple, convenient, and personalized.
          </p>

          <p className="flex items-start gap-3">
            <FaUserMd className="text-teal-600 mt-1 text-xl" />
            From vaccinations and prescribing to custom compounding and free
            delivery, our pharmacists are here to support you every step of the way.
          </p>

          <ul className="list-disc list-inside space-y-2 pl-6 pt-2 text-gray-800">
            <li>Prescriptions, refills, & medication reviews</li>
            <li>Vaccinations & injection services</li>
            <li>Assessment & prescribing for minor conditions</li>
            <li>Custom compounding & blister packaging</li>
            <li>Free same-day prescription delivery in Calgary</li>
          </ul>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section
        className="relative z-10 bg-gradient-to-br from-teal-300 via-white to-teal-100 py-20 px-6"
        data-aos="fade-up"
      >
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-extrabold text-center text-teal-700 mb-14">
            Expert Care with Convenient Access
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {pharmacyServices.map(({ title, description, icon }) => (
              <div
                key={title}
                className="group bg-teal-600 rounded-2xl shadow-lg p-7 text-white text-center
                transition-all duration-300 hover:scale-[1.04] hover:shadow-2xl
                flex flex-col items-center"
              >
                <div className="mb-4">{icon}</div>
                <h4 className="text-lg md:text-xl font-semibold mb-2">{title}</h4>
                <p className="text-sm md:text-base leading-relaxed opacity-90">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const pharmacyServices = [
  {
    title: "Vaccinations & Immunizations",
    icon: <FaSyringe className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Flu, shingles, pneumonia, childhood & travel vaccines — stay protected year-round.",
  },
  {
    title: "Injection Services",
    icon: <FaPrescriptionBottleAlt className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "We administer prescribed injectable medications — most without an appointment.",
  },
  {
    title: "Consultation",
    icon: <FaUserMd className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Private medication reviews, safety checks, dosage guidance, and treatment planning.",
  },
  {
    title: "Prescribing Pharmacist",
    icon: <FaStethoscope className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Assessment & prescriptions for minor ailments — UTIs, cold sores, rashes, and more.",
  },
  {
    title: "OAT (Opioid Agonist Treatment)",
    icon: <FaNotesMedical className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Discreet, supportive, and consistent care including supervised dosing.",
  },
  {
    title: "Compounding",
    icon: <FaPills className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Custom medications for pediatrics, dermatology, hormone therapy, and allergies.",
  },
  {
    title: "Compliance Packaging",
    icon: <FaFilePrescription className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Blister packs organized by day & time — perfect for seniors or complex regimens.",
  },
  {
    title: "Travel Health Services",
    icon: <FaPlaneDeparture className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Vaccines, prophylaxis, and travel consultations to keep you safe abroad.",
  },
  {
    title: "Home Care Supplies",
    icon: <FaHome className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Mobility aids, wound care, incontinence, and home medical equipment.",
  },
  {
    title: "Free Local Delivery",
    icon: <FaTruck className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Same-day prescription delivery anywhere in Calgary — fast & reliable.",
  },
  {
    title: "Uninsured Services",
    icon: <FaFileInvoice className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Private-pay options for services not covered by public health insurance.",
  },
];
